import { Stack } from '@mui/material';

import { AddAutomationButton } from './components/AddAutomationButton';
import { AutomationListCTA } from './components/AutomationListCTA';
import { AutomationListProps, Automation } from '../types';
import { ATSAutomationListTestIds } from 'data-testids/ATS';
import { useAutomation } from '../context/AutomationContext';
import { AutomationListVariant, AutomationModalPaneOptions } from '../enums';
import { AutomationListItemSkeleton, AutomationListItem } from './components/AutomationListItem';
import { AutomationContainer, AutomationQuestionText } from '../styles';
import { groupByQuestionId } from '../utils/groupByQuestionId';
import { useQueryGetAutomationsListData } from '../api/listQueries';
import { useState } from 'react';
import { SortableList } from 'sharedComponents/List/SortableList/SortableList';
import { ACTION_TYPES } from '../reducers/automationReducer';
import { DragHandle, SortableItem } from 'sharedComponents/List/SortableList/components';

export const AutomationList: React.FC<AutomationListProps> = ({
  variant,
  automationState,
  automationDispatch,
}) => {
  const { onChangeModalPane, questions, roleId } = useAutomation();
  const { data, isLoading } = useQueryGetAutomationsListData(roleId);
  const sortedAutomations = data && groupByQuestionId(data);
  const groupedAutomations = sortedAutomations && Object.entries(sortedAutomations);
  const isEditable = variant === AutomationListVariant.EDITABLE;
  const [reorderedGroups, setReorderedGroups] = useState<[string, Automation[]][] | null>(
    groupedAutomations
  );

  const automationGroups = reorderedGroups || groupedAutomations || [];

  const sortableItems = automationGroups.map(([questionId, automations], index) => ({
    id: `question-${questionId}`,
    index,
    questionId,
    automations,
  }));
  const handleGroupsChange = (newItems: typeof sortableItems) => {
    const newGroups: [string, Automation[]][] = newItems.map((item) => [
      item.questionId,
      item.automations,
    ]);

    setReorderedGroups(newGroups);

    const updatedAutomations: Automation[] = [];
    newItems.forEach((item, groupIndex) => {
      item.automations.forEach((automation) => {
        updatedAutomations.push({
          ...automation,
          order: groupIndex,
        });
      });
    });

    if (updatedAutomations.length > 0) {
      automationDispatch({
        type: ACTION_TYPES.SET_AUTOMATION_LIST,
        payload: {
          automations: updatedAutomations,
          hasUnsavedChanges: true,
        },
      });
    }
  };

  return (
    <Stack direction="column" data-testid={ATSAutomationListTestIds.LIST_PANE}>
      <Stack alignItems="flex-start">
        {isLoading ? (
          <>
            <AutomationListItemSkeleton />
            <AutomationListItemSkeleton />
          </>
        ) : (
          <SortableList
            items={sortableItems}
            onChange={handleGroupsChange}
            itemRenderer={(item) => (
              <SortableItem key={item.id} id={item.id} isEditable={isEditable}>
                <AutomationContainer direction="row" gap={3}>
                  {variant === AutomationListVariant.EDITABLE && (
                    <DragHandle sx={{ opacity: '0.56', maxHeight: '30px' }} />
                  )}
                  <Stack direction="column" sx={{ width: '100%' }}>
                    <AutomationQuestionText listVariant={variant}>
                      {questions.find((q) => q.id === parseInt(item.questionId))?.prompt}
                    </AutomationQuestionText>
                    {item.automations.map((automation) => (
                      <AutomationListItem
                        variant={variant}
                        key={automation.id}
                        automation={automation}
                      />
                    ))}
                  </Stack>
                </AutomationContainer>
              </SortableItem>
            )}
          />
        )}
        {isEditable && (
          <AddAutomationButton
            onClick={() => onChangeModalPane(AutomationModalPaneOptions.CREATE)}
          />
        )}
      </Stack>
      {isEditable && (
        <AutomationListCTA
          automationState={automationState}
          automationDispatch={automationDispatch}
        />
      )}
    </Stack>
  );
};
